import { DESIGN_WIDTH } from '../BroadcastFrame.js';

/**
 * Coordinate helpers for stage reveals — the sharp edge every reveal screen hits.
 *
 * The Stage is authored in a fixed 1920×1080 DESIGN space that BroadcastFrame
 * scales to fit the pane. Two different subsystems want a target's position in two
 * DIFFERENT coordinate spaces:
 *   - Spotlight.snap()/ease() want DESIGN-space px (relative to the 1920×1080 root)
 *   - burst() wants NORMALIZED 0..1 VIEWPORT coords (fraction of window)
 * so a screen that spotlights + bursts the same element must convert its on-screen
 * DOM rect BOTH ways. These helpers centralize that so screens don't re-derive it
 * (and get the scale factor wrong).
 *
 * `root` is the 1920×1080 design div (BroadcastFrame's inner scaled container, or
 * any element that IS that box). Pass the element you measure against.
 */

export interface DesignPoint {
  /** x in design-space px. */
  x: number;
  /** y in design-space px. */
  y: number;
}

export interface ViewportPoint {
  /** x as 0..1 fraction of the window width. */
  x: number;
  /** y as 0..1 fraction of the window height. */
  y: number;
}

/**
 * Center of `el` in DESIGN-space px (for Spotlight). Maps the element's on-screen
 * rect back through the frame's scale. Returns null if either element is missing.
 */
export function centerInDesign(root: HTMLElement | null, el: HTMLElement | undefined | null): DesignPoint | null {
  if (!root || !el) return null;
  const rootR = root.getBoundingClientRect();
  const r = el.getBoundingClientRect();
  const scale = rootR.width / DESIGN_WIDTH || 1;
  return {
    x: (r.left + r.width / 2 - rootR.left) / scale,
    y: (r.top + r.height / 2 - rootR.top) / scale,
  };
}

/**
 * Center of `el` as NORMALIZED 0..1 viewport coords (for burst). Falls back to the
 * frame center (0.5, 0.5) if the element is missing.
 */
export function centerInViewport(el: HTMLElement | undefined | null): ViewportPoint {
  const r = el?.getBoundingClientRect();
  if (!r || typeof window === 'undefined') return { x: 0.5, y: 0.5 };
  return {
    x: (r.left + r.width / 2) / window.innerWidth,
    y: (r.top + r.height / 2) / window.innerHeight,
  };
}

// Named aliases matching the reveal-shell vocabulary (a "bust" is the usual
// reveal target, but these work for any element). Same functions.
/** Alias of centerInDesign — the element's center in 1920×1080 design px (Spotlight). */
export const bustCenterDesign = centerInDesign;
/** Alias of centerInViewport — the element's center as 0..1 viewport coords (burst). */
export const bustCenterViewport = centerInViewport;
